import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, MapPinned, UserCog } from "lucide-react";

const DIAS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];
const DIAS_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function NovaVenda() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [params] = useSearchParams();
  const [clientes, setClientes] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [cobradores, setCobradores] = useState([]);
  const [form, setForm] = useState({
    cliente_id: params.get("cliente") || "",
    descricao: "",
    valor_total: "",
    entrada: "0",
    num_parcelas: "4",
    valor_parcela: "",
    frequencia: "mensal",
    primeira_data: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    observacoes: "",
  });
  // Qual campo o usuário editou por último: "valor_parcela" | "num_parcelas"
  const [lastEdited, setLastEdited] = useState("num_parcelas");
  // ajustes feitos no cliente antes de salvar a venda
  const [rotaOverride, setRotaOverride] = useState(""); // "" = usar valor atual do cliente
  const [cobradorOverride, setCobradorOverride] = useState("");
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/clientes").then((r) => setClientes(r.data));
    api.get("/rotas").then((r) => setRotas(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/staff").then((r) => setCobradores(r.data.filter((s) => s.roles?.cobrador && s.active))).catch(() => {});
    }
    // prefill from AI scan
    const draft = sessionStorage.getItem("cf_ai_draft");
    if (draft) {
      try {
        const d = JSON.parse(draft);
        const hasVP = d.valor_parcela != null && d.valor_parcela !== "" && Number(d.valor_parcela) > 0;
        setForm((f) => ({
          ...f,
          cliente_id: d.cliente_id || f.cliente_id,
          descricao: d.descricao || f.descricao,
          valor_total: d.valor_total != null && d.valor_total !== "" ? String(d.valor_total) : f.valor_total,
          entrada: d.entrada != null && d.entrada !== "" ? String(d.entrada) : f.entrada,
          num_parcelas: d.num_parcelas != null && d.num_parcelas !== "" ? String(d.num_parcelas) : f.num_parcelas,
          valor_parcela: hasVP ? String(d.valor_parcela) : f.valor_parcela,
          frequencia: d.frequencia || f.frequencia,
          primeira_data: d.primeira_data || f.primeira_data,
          observacoes: d.observacoes || f.observacoes,
        }));
        if (hasVP) setLastEdited("valor_parcela");
      } catch (_e) { /* ignore */ }
      sessionStorage.removeItem("cf_ai_draft");
    }
    // eslint-disable-next-line
  }, []);

  const clienteSel = useMemo(() => clientes.find((c) => c.id === form.cliente_id), [clientes, form.cliente_id]);

  // Resetar overrides toda vez que o cliente muda
  useEffect(() => {
    setRotaOverride("");
    setCobradorOverride("");
  }, [form.cliente_id]);

  const rotaIdEfetiva = rotaOverride !== "" ? (rotaOverride || null) : (clienteSel?.rota_id || null);
  const cobradorIdEfetivo = cobradorOverride !== "" ? (cobradorOverride || null) : (clienteSel?.assigned_cobrador_id || null);
  const rotaSel = rotaIdEfetiva ? rotas.find((r) => r.id === rotaIdEfetiva) : null;

  const restante = Math.round(Math.max(0, (parseFloat(form.valor_total) || 0) - (parseFloat(form.entrada) || 0)) * 100) / 100;

  // Estratégia dinâmica: se o usuário informou o valor da parcela, derivamos quantidade.
  // Caso contrário, derivamos valor_parcela dividindo o restante por num_parcelas.
  const vpInput = parseFloat(form.valor_parcela) || 0;
  const numInput = Math.max(1, parseInt(form.num_parcelas || "1") || 1);
  let numCalc = numInput;
  let parcelaBase = 0;
  let ultimaParcela = 0;
  if (lastEdited === "valor_parcela" && vpInput > 0 && restante > 0) {
    numCalc = Math.max(1, Math.ceil(Math.round((restante / vpInput) * 10000) / 10000));
    parcelaBase = vpInput;
    ultimaParcela = Math.round((restante - vpInput * (numCalc - 1)) * 100) / 100;
    if (ultimaParcela < 0) ultimaParcela = 0;
  } else {
    numCalc = numInput;
    parcelaBase = Math.round((restante / numCalc) * 100) / 100;
    ultimaParcela = Math.round((restante - parcelaBase * (numCalc - 1)) * 100) / 100;
  }
  const divisaoExata = Math.abs(ultimaParcela - parcelaBase) < 0.005;

  // Preview da data de cobrança da 1ª parcela
  const previewCobranca = (() => {
    if (!rotaSel || !form.primeira_data) return null;
    try {
      const v = new Date(form.primeira_data + "T00:00:00");
      const jsWd = v.getDay();
      const wd = (jsWd + 6) % 7;
      const offsetBack = (wd - rotaSel.dia_semana + 7) % 7;
      const rotaDate = new Date(v);
      rotaDate.setDate(v.getDate() - offsetBack);
      const result = new Date(rotaDate);
      if (offsetBack > 3) result.setDate(result.getDate() + 7);
      return result.toISOString().slice(0, 10);
    } catch { return null; }
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) return toast.error("Selecione um cliente");
    if (!clienteSel) return toast.error("Cliente não encontrado");
    setSaving(true);
    try {
      // 1) Se admin/vendedor mudou rota/cobrador do cliente, atualiza antes da venda
      const needsUpdate =
        (rotaOverride !== "" && (rotaOverride || null) !== (clienteSel.rota_id || null)) ||
        (isAdmin && cobradorOverride !== "" && (cobradorOverride || null) !== (clienteSel.assigned_cobrador_id || null));
      if (needsUpdate) {
        const payload = {
          nome: clienteSel.nome,
          telefone: clienteSel.telefone || "",
          bairro: clienteSel.bairro || "",
          endereco: clienteSel.endereco || "",
          observacoes: clienteSel.observacoes || "",
          rota_id: rotaIdEfetiva || null,
        };
        if (isAdmin) payload.assigned_cobrador_id = cobradorIdEfetivo || null;
        await api.put(`/clientes/${clienteSel.id}`, payload);
      }
      const body = {
        ...form,
        valor_total: parseFloat(form.valor_total),
        entrada: parseFloat(form.entrada) || 0,
        num_parcelas: numCalc,
        valor_parcela: lastEdited === "valor_parcela" && vpInput > 0 ? vpInput : null,
      };
      const r = await api.post("/vendas", body);
      toast.success("Venda cadastrada!");
      nav(`/clientes/${r.data.cliente_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900 mb-1">Nova Venda</h1>
      <p className="text-sm text-stone-500 mb-5">Cadastre uma venda parcelada em segundos.</p>

      <form onSubmit={submit} className="carne-card p-5 space-y-4" data-testid="sale-create-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Cliente*</label>
          <select
            data-testid="select-sale-client"
            value={form.cliente_id}
            onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
            className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            required
          >
            <option value="">Selecione…</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>

        {clienteSel && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1">
                <MapPinned className="w-3 h-3" /> Rota de cobrança
              </label>
              <select
                data-testid="select-sale-rota"
                value={rotaOverride !== "" ? rotaOverride : (clienteSel.rota_id || "")}
                onChange={(e) => setRotaOverride(e.target.value)}
                className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
              >
                <option value="">— Sem rota —</option>
                {rotas.map((r) => (
                  <option key={r.id} value={r.id}>{r.nome} ({DIAS_SHORT[r.dia_semana]})</option>
                ))}
              </select>
              {rotas.length === 0 && (
                <div className="text-[11px] text-stone-500 mt-1">
                  Nenhuma rota ainda. {isAdmin && <a href="/rotas" className="text-[color:var(--terracotta)] font-bold">Criar rota</a>}
                </div>
              )}
            </div>
            {isAdmin && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1">
                  <UserCog className="w-3 h-3" /> Cobrador responsável
                </label>
                <select
                  data-testid="select-sale-cobrador"
                  value={cobradorOverride !== "" ? cobradorOverride : (clienteSel.assigned_cobrador_id || "")}
                  onChange={(e) => setCobradorOverride(e.target.value)}
                  className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
                >
                  <option value="">— Nenhum —</option>
                  {cobradores.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>
                  ))}
                </select>
                {cobradores.length === 0 && (
                  <div className="text-[11px] text-stone-500 mt-1">
                    Nenhum cobrador. <a href="/equipe" className="text-[color:var(--terracotta)] font-bold">Cadastrar</a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Item / Descrição*</label>
          <input
            data-testid="input-sale-items"
            placeholder="Ex: Jogo de Panelas 5 peças"
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Valor Total (R$)*</label>
            <input
              data-testid="input-sale-total-value"
              type="number"
              step="0.01"
              value={form.valor_total}
              onChange={(e) => setForm({ ...form, valor_total: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
              required
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Entrada (R$)</label>
            <input
              data-testid="input-sale-downpayment"
              type="number"
              step="0.01"
              value={form.entrada}
              onChange={(e) => setForm({ ...form, entrada: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Valor da Parcela (R$)</label>
            <input
              data-testid="input-installment-value"
              type="number"
              step="0.01"
              min="0"
              placeholder="Opcional"
              value={form.valor_parcela}
              onChange={(e) => { setForm({ ...form, valor_parcela: e.target.value }); setLastEdited("valor_parcela"); }}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Nº Parcelas*</label>
            <input
              data-testid="input-installment-count"
              type="number"
              min="1"
              max="60"
              value={lastEdited === "valor_parcela" && vpInput > 0 ? numCalc : form.num_parcelas}
              readOnly={lastEdited === "valor_parcela" && vpInput > 0}
              onChange={(e) => { setForm({ ...form, num_parcelas: e.target.value, valor_parcela: "" }); setLastEdited("num_parcelas"); }}
              className={`mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 ${lastEdited === "valor_parcela" && vpInput > 0 ? "bg-stone-100" : "bg-white"}`}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Frequência</label>
            <select
              data-testid="select-installment-frequency"
              value={form.frequencia}
              onChange={(e) => setForm({ ...form, frequencia: e.target.value })}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            >
              <option value="semanal">Semanal</option>
              <option value="quinzenal">Quinzenal</option>
              <option value="mensal">Mensal</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-stone-500">1ª Data de vencimento*</label>
          <input
            data-testid="input-first-due"
            type="date"
            min={today}
            value={form.primeira_data}
            onChange={(e) => setForm({ ...form, primeira_data: e.target.value })}
            className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            required
          />
        </div>

        <textarea
          data-testid="input-sale-obs"
          placeholder="Observações (opcional)"
          value={form.observacoes}
          onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
          className="w-full min-h-[70px] p-3 rounded-xl border border-stone-200 bg-white"
        />

        <div className="bg-[color:var(--terracotta-light)] rounded-xl p-3 text-sm" data-testid="preview-parcelas">
          <div className="flex justify-between"><span className="text-stone-600">Valor total:</span><span className="font-mono-num font-bold">{money(parseFloat(form.valor_total) || 0)}</span></div>
          {(parseFloat(form.entrada) || 0) > 0 && (
            <div className="flex justify-between"><span className="text-stone-600">Entrada:</span><span className="font-mono-num font-bold">{money(parseFloat(form.entrada) || 0)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-stone-600">Valor parcelado:</span><span className="font-mono-num font-bold">{money(restante)}</span></div>
          <div className="flex justify-between"><span className="text-stone-600">Quantidade:</span><span className="font-mono-num font-bold">{numCalc} parcela{numCalc > 1 ? "s" : ""}</span></div>
          <div className="flex justify-between"><span className="text-stone-600">Valor da parcela:</span><span className="font-mono-num font-bold text-[color:var(--terracotta)]">{money(parcelaBase)}</span></div>
          {!divisaoExata && numCalc > 1 && (
            <div className="mt-1 pt-1 border-t border-[color:var(--terracotta)]/20 text-[12px] text-stone-700" data-testid="preview-ultima-diferente">
              Resumo: <b>{numCalc - 1}× {money(parcelaBase)}</b> + <b>1× {money(ultimaParcela)}</b> (última parcela diferente)
            </div>
          )}
          {divisaoExata && numCalc > 1 && (
            <div className="mt-1 pt-1 border-t border-[color:var(--terracotta)]/20 text-[12px] text-stone-700">
              Última parcela: <b>{money(ultimaParcela)}</b>
            </div>
          )}
          {rotaSel && previewCobranca && (
            <div className="flex justify-between mt-1 pt-1 border-t border-[color:var(--terracotta)]/20" data-testid="preview-cobranca">
              <span className="text-stone-600">Rota <b>{rotaSel.nome}</b> ({DIAS_FULL[rotaSel.dia_semana]}):</span>
              <span className="font-mono-num font-bold text-[color:var(--terracotta)]">cobra em {previewCobranca.split("-").reverse().join("/")}</span>
            </div>
          )}
          {!rotaSel && clienteSel && (
            <div className="text-[11px] text-stone-500 mt-1">Cliente sem rota — cobrança no próprio dia do vencimento.</div>
          )}
        </div>

        <button data-testid="btn-submit-sale" type="submit" disabled={saving} className="btn-primary w-full">
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Cadastrar venda"}
        </button>
      </form>
    </div>
  );
}
